import "./MediaMix.scss";
import React, { useEffect, useState } from "react";
import * as R from "ramda";
import { parseISO, format } from "date-fns";
import { getNtbTableHeaders, getProspectiveTableHeaders } from "./MediaMixConfig";
import { useCompanyInfo } from "../../../redux/company";
import { BPMTable, FullPageSpinner, Page } from "../../../Components";
import { abbreviateNumber, formatMoneyAsInt } from "../../../utils/format-utils";
import { AmcLambdaFetch, awaitJSON } from "../../../utils/fetch-utils";
import AreaAndBarChart from "../../../Components/AreaAndBarChart/AreaAndBarChart";
import {
  formatDate,
  MonthPicker,
  startDateRange,
} from "../../../Components/MonthPicker/MonthPicker";
import { useSetError } from "../../../redux/modals";
import { RouteComponentProps } from "@reach/router";
import { AMC_TEST_INSTANCE_ID } from "../../Insights";

export interface AmcAvailableFilters {
  granularity: string[];
  months?: string[];
  weeks?: string[];
}

interface SharedMediaMixData {
  avgOrderSize: number;
  purchased: number;
  reach: number;
}

interface MediaMixTableData extends SharedMediaMixData {
  mediaType: string;
  purchaseRate: number;
}

interface MediaMixGraphData extends SharedMediaMixData {
  date: string; // ISO date string
}

export interface GetAmcMediaMixResponse {
  availableFilters: AmcAvailableFilters;
  newToBrandTable: MediaMixTableData[];
  newToBrandGraph: MediaMixGraphData[];
  prospectiveTable: MediaMixTableData[];
  prospectiveGraph: MediaMixGraphData[];
}

/*eslint-disable no-empty-pattern*/
const AMCMediaMix = ({}: RouteComponentProps): JSX.Element => {
  let { cid, amc_instance_id: amcInstanceId } = useCompanyInfo();
  if (cid === "tinuititest") {
    amcInstanceId = AMC_TEST_INSTANCE_ID;
  }
  const setError = useSetError();
  // Previous month reports not prepared until the 15th of current month
  const monthsBack = new Date().getDate() < 15 ? 2 : 1;
  const [startDateSelection, updateStartDateSelection] = useState(startDateRange(monthsBack + 2));
  const [endDateSelection, updateEndDateSelection] = useState(startDateRange(monthsBack));
  const [dateOptions, updateDateOptions] = useState([{ value: "" }]);
  const [dateRange, updateDateRange] = useState({
    endDate: formatDate(endDateSelection, "end"),
    startDate: formatDate(startDateSelection, "start"),
  });

  const [mediaMixData, setMediaMixData] = useState<GetAmcMediaMixResponse | null>(null);
  const [maxValues, setMaxValues] = useState({
    reach: 0,
    proPurchased: 0,
    proPurchaseRate: 0,
    proOrderSize: 0,
    ntbPurchased: 0,
    ntbPurchaseRate: 0,
    ntbOrderSize: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const sanitizeTableData = (rows: MediaMixTableData[]): MediaMixTableData[] => {
    return rows.map(row => ({
      ...row,
      reach: Number(row.reach),
      purchased: Number(row.purchased),
      purchaseRate: Number(row.purchaseRate),
      avgOrderSize: Number(row.avgOrderSize),
    }));
  };

  const sanitizeGraphData = (rows: MediaMixGraphData[]): MediaMixGraphData[] => {
    return rows.map(row => {
      const formattedDate = format(parseISO(row.date), "MMM dd, yyyy");
      return {
        ...row,
        reach: Number(row.reach),
        purchased: Number(row.purchased),
        avgOrderSize: Number(row.avgOrderSize),
        date: formattedDate,
      };
    });
  };

  useEffect(() => {
    const fetchMediaMix = async () => {
      if (!amcInstanceId) {
        return;
      }
      try {
        setIsLoading(true);
        const res = await AmcLambdaFetch("/getMediaMixReport", {
          params: {
            amcInstanceId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            duration: "monthly",
          },
        });
        const json: GetAmcMediaMixResponse = await awaitJSON(res);
        const sanitized: GetAmcMediaMixResponse = {
          ...json,
          prospectiveTable: sanitizeTableData(json.prospectiveTable),
          newToBrandTable: sanitizeTableData(json.newToBrandTable),
          prospectiveGraph: sanitizeGraphData(json.prospectiveGraph),
          newToBrandGraph: sanitizeGraphData(json.newToBrandGraph),
        };
        setMediaMixData(sanitized);

        // Calculate and set max values here to mimic previous reactive variable behavior
        const getMax = (arr: MediaMixTableData[], key: keyof SharedMediaMixData) =>
          Math.max(...arr.map(row => (row.mediaType !== "Grand Total" ? row[key] : 0)));

        setMaxValues({
          reach: getMax(json.prospectiveTable, "reach"),
          proPurchased: getMax(json.prospectiveTable, "purchased"),
          proPurchaseRate: Math.max(...json.prospectiveTable.map(row => row.purchaseRate)),
          proOrderSize: getMax(json.prospectiveTable, "avgOrderSize"),
          ntbPurchased: getMax(json.newToBrandTable, "purchased"),
          ntbPurchaseRate: Math.max(...json.newToBrandTable.map(row => row.purchaseRate)),
          ntbOrderSize: getMax(json.newToBrandTable, "avgOrderSize"),
        });
      } catch (err) {
        setError({
          title: "Error fetching Media Mix data",
          message: "There was an error fetching the Media Mix data. Please try again later.",
        });
        setMediaMixData(null);
      } finally {
        setIsLoading(false);
      }
    };
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      setError({
        title: "Invalid Date Range",
        message: "Please select a valid date range where the start date is before the end date.",
      });
      const start = startDateRange(monthsBack + 2);
      const end = startDateRange(monthsBack);
      updateStartDateSelection(start);
      updateEndDateSelection(end);
      updateDateRange({
        startDate: formatDate(start, "start"),
        endDate: formatDate(end, "end"),
      });
      return;
    }
    fetchMediaMix();
  }, [amcInstanceId, dateRange, monthsBack, setError]);

  const formatTotalCells = data => {
    data.avgOrderSize = `${formatMoneyAsInt(data.avgOrderSize)}`;
    data.purchaseRate = `${data.purchaseRate}%`;
  };

  const bottomRenderer = ({ data, style, classes }): JSX.Element => {
    let newData;
    newData = typeof data === "string" ? data : abbreviateNumber(parseFloat(data));

    return (
      <div className={classes.join(" ")} style={style}>
        {newData}
      </div>
    );
  };

  const prospectiveTableHeaders = getProspectiveTableHeaders(maxValues);
  const ntbTableHeaders = getNtbTableHeaders(maxValues);

  // Cloned to make formatting changes for avgOrderSize and pruchaseRate
  const prospectivePinnedBottomRowData = R.clone(
    mediaMixData?.prospectiveTable.find(row => row.mediaType === "Grand Total")
  );
  if (prospectivePinnedBottomRowData) {
    formatTotalCells(prospectivePinnedBottomRowData);
  }

  const prospectiveTableData = mediaMixData?.prospectiveTable.filter(
    row => row.mediaType !== "Grand Total"
  );

  // Cloned to make formatting changes for avgOrderSize and pruchaseRate
  const ntbPinnedBottomRowData = R.clone(
    mediaMixData?.newToBrandTable.filter(row => row.mediaType === "Grand Total")[0]
  );
  if (ntbPinnedBottomRowData) {
    formatTotalCells(ntbPinnedBottomRowData);
  }

  const ntbTableData = mediaMixData?.newToBrandTable.filter(row => row.mediaType !== "Grand Total");

  useEffect(() => {
    if (mediaMixData?.availableFilters?.months) {
      updateDateOptions(mediaMixData.availableFilters.months.map(m => ({ value: m })));
    }
  }, [mediaMixData]);

  return (
    <Page minHeight="600px" pageType="AMC Media Mix" title="AMC Media Mix">
      {!mediaMixData || (isLoading && <FullPageSpinner />)}
      <div className="media-mix-page">
        <div
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "15px",
          }}
        >
          <div
            className="section-head"
            style={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              Which media mix is most effective in converting{" "}
              <b style={{ fontWeight: 500 }}>prospective shoppers into customers</b>?
            </div>
          </div>
          <MonthPicker
            dateOptions={dateOptions}
            endDateSelection={endDateSelection}
            startDateSelection={startDateSelection}
            updateDateRange={updateDateRange}
            updateEndDateSelection={updateEndDateSelection}
            updateStartDateSelection={updateStartDateSelection}
          />
        </div>
        <div className="basic-card" style={{ marginBottom: "30px" }}>
          <div className="table-wrapper">
            {mediaMixData ? (
              <BPMTable
                data={prospectiveTableData}
                filterBar={false}
                headers={prospectiveTableHeaders}
                noRowsRenderer={() => <div className="noFailures">No failures to show</div>}
                totals={prospectivePinnedBottomRowData}
                totalsRenderer={bottomRenderer}
              />
            ) : (
              <FullPageSpinner />
            )}
          </div>
          <div className="graph-wrapper">
            <AreaAndBarChart
              areaDataKey="purchased"
              barDataKey="reach"
              data={mediaMixData?.prospectiveGraph || []}
              xAxisDataKey="date"
              barYAxisDataKey="reach"
              barYAxisLabel="Shoppers Reached"
              areaYAxisDataKey="purchased"
              areaYAxisLabel="Shoppers Purchased"
            />
          </div>
        </div>
        <div className="section-head">
          Which media mix is most effective in converting{" "}
          <b style={{ fontWeight: 500 }}>New-to-Brand shoppers into customers</b>?
        </div>

        <div className="basic-card">
          <div className="table-wrapper">
            {mediaMixData ? (
              <BPMTable
                data={ntbTableData}
                filterBar={false}
                headers={ntbTableHeaders}
                noRowsRenderer={() => <div className="noFailures">No failures to show</div>}
                totals={ntbPinnedBottomRowData}
                totalsRenderer={bottomRenderer}
              />
            ) : (
              <FullPageSpinner />
            )}
          </div>
          <div className="graph-wrapper">
            <AreaAndBarChart
              areaDataKey="purchased"
              barDataKey="reach"
              data={mediaMixData?.newToBrandGraph || []}
              xAxisDataKey="date"
              barYAxisDataKey="reach"
              barYAxisLabel="Shoppers Reached"
              areaYAxisDataKey="purchased"
              areaYAxisLabel="Shoppers Purchased"
            />
          </div>
        </div>
      </div>
    </Page>
  );
};

AMCMediaMix.propTypes = {};

export default AMCMediaMix;
